import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import { BitbucketIcon } from '@atlaskit/logo';
import type { ReactNode } from 'react';

import { envType } from '../../../common/types';
import type { Action } from '../../../common/types';
import { getEnvType } from '../../../utils';
import {
	BITBUCKET_MANAGE_LINK,
	BITBUCKET_SETUP_GUIDE_LINK,
	GITHUB_INSTALL_LINK,
	GITHUB_SETUP_GUIDE_LINK,
	PROD_BITBUCKET_DOMAIN,
	STG_BITBUCKET_DOMAIN,
} from '../../../utils/constants';
import { GithubIcon } from '../common/icons';

export const PROVIDER_IDS = {
	GITHUB_STG: 'com.github.integration.staging',
	GITHUB_PROD: 'com.github.integration.production',
	BITBUCKET: 'bitbucket',
};

export type Integration = {
	title: string;
	description: string;
	icon: ReactNode;
	primaryAction: Action;
	secondaryAction?: Action;
	id: string;
	testId?: string;
};

export const getManageBitbucketLink = () => {
	const currentEnv = getEnvType();

	if (currentEnv === envType.LOCAL) {
		return process.env.REMOTE_DOMAIN + BITBUCKET_MANAGE_LINK;
	}

	return `${window.location.protocol}//${window.location.host}${BITBUCKET_MANAGE_LINK}`;
};

export const getBitbucketBaseUrl = () => {
	const currentEnv = getEnvType();
	const domain = currentEnv === envType.PROD ? PROD_BITBUCKET_DOMAIN : STG_BITBUCKET_DOMAIN;

	return `https://${domain}`;
};

export const integrations: Integration[] = [
	{
		id: PROVIDER_IDS.BITBUCKET,
		title: 'Bitbucket for Jira',
		description: 'Set up a DVCS connection between Bitbucket and Jira.',
		icon: <BitbucketIcon appearance="brand" size="small" />,
		primaryAction: {
			icon: LinkExternalIcon,
			label: 'Connect Bitbucket',
			href: {
				link: getManageBitbucketLink(),
			},
			analyticEvent: {
				action: 'clicked',
				actionSubject: 'link',
				actionSubjectId: 'connect-bbc',
				source: 'home-page-setup',
			},
		},
		secondaryAction: {
			label: 'How to connect Bitbucket to Jira',
			href: {
				link: BITBUCKET_SETUP_GUIDE_LINK,
			},
			analyticEvent: {
				action: 'clicked',
				actionSubject: 'link',
				actionSubjectId: 'connect-bbc-info-link',
				source: 'home-page-setup',
			},
		},
		testId: 'bitbucket-integration',
	},
	{
		id: PROVIDER_IDS.GITHUB_STG,
		title: 'GitHub',
		description: 'Use the GitHub for Jira app to connect.',
		icon: <GithubIcon />,
		primaryAction: {
			icon: LinkExternalIcon,
			label: 'Install GitHub for Jira',
			href: {
				link: GITHUB_INSTALL_LINK,
			},
			analyticEvent: {
				action: 'clicked',
				actionSubject: 'link',
				actionSubjectId: 'install-gh4j-link',
				source: 'home-page-setup',
			},
		},
		secondaryAction: {
			label: 'How to connect GitHub for Jira',
			href: {
				link: GITHUB_SETUP_GUIDE_LINK,
			},
			analyticEvent: {
				action: 'clicked',
				actionSubject: 'link',
				actionSubjectId: 'install-gh4j-info-link',
				source: 'home-page-setup',
			},
		},
		testId: 'github-integration',
	},
	{
		id: PROVIDER_IDS.GITHUB_PROD,
		title: 'GitHub',
		description: 'Setup a connection between GitHub and Jira',
		icon: <GithubIcon />,
		primaryAction: {
			icon: LinkExternalIcon,
			label: 'Install GitHub for Jira',
			href: {
				link: GITHUB_INSTALL_LINK,
			},
			analyticEvent: {
				action: 'clicked',
				actionSubject: 'link',
				actionSubjectId: 'install-gh4j-link',
				source: 'home-page-setup',
			},
		},
		secondaryAction: {
			label: 'How to connect GitHub for Jira',
			href: {
				link: GITHUB_SETUP_GUIDE_LINK,
			},
			analyticEvent: {
				action: 'clicked',
				actionSubject: 'link',
				actionSubjectId: 'install-gh4j-info-link',
				source: 'home-page-setup',
			},
		},
		testId: 'github-integration',
	},
];

export const getRequiredProviders = () => {
	const currentEnv = getEnvType();

	switch (currentEnv) {
		case envType.PROD:
			return [PROVIDER_IDS.GITHUB_PROD, PROVIDER_IDS.BITBUCKET];
		case envType.DEV || envType.STAGING:
			return [PROVIDER_IDS.GITHUB_STG, PROVIDER_IDS.BITBUCKET];
		default:
			return [PROVIDER_IDS.GITHUB_STG, PROVIDER_IDS.BITBUCKET];
	}
};
